import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Award,
  Banknote,
  CalendarClock,
  FileText,
  Gift,
  Home,
  KeyRound,
  LogOut,
  Menu,
  MessageCircle,
  MessageSquare,
  Moon,
  Network,
  PanelLeft,
  PanelLeftClose,
  Radio,
  Repeat,
  Settings as SettingsIcon,
  Share2,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Sun,
  Ticket,
  TrendingUp,
  Users,
  Video,
  Wallet,
} from 'lucide-react';
import { Avatar, Sheet, SheetContent, cn } from '@mlm/ui';
import { logout, getOrderActionSummary, getMe, getSlotWallet, getGenealogy } from '../lib/api';
import { getChatUnreadCount } from '../lib/chat-api';
import { useAuthStore } from '../lib/auth-store';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { useDarkAvailable, useThemeMode } from '../lib/theme-mode';
import { initialsFromName } from '../lib/format';
import { SwitchSlotMenu } from './switch-slot-menu';
import { CopyButton } from './copy-button';
import { MessagesBell } from './messages-bell';
import { NotificationsBell } from './notifications-bell';
import { useBrand, useTenantConfig } from '../storefront/lib/hooks';
import { useBrandLogo } from '../storefront/components/storefront-logo';
import { TermsReacceptGate } from './terms-reaccept-gate';
import { UnpaidOrdersGate } from './unpaid-orders-gate';
import { ImpersonationBanner } from './impersonation-banner';
import { useTutorialEnabled } from '../lib/tutorial/use-tutorial-enabled';
import { TutorialNavButton } from './tutorial/tutorial-menu';
import { Header } from '../storefront/components/header';
import { HAS_BESPOKE_LANDING } from '@app/storefront/tenant-landing';

// White-label sidebar brand — build-time seed, set per client from TENANT_NAME
// by ops/deploy-client.sh (VITE_BRAND). Shown on first paint before the runtime
// brand (useBrand) resolves; neutral fallback in dev / unbranded builds.
const BRAND = (import.meta.env.VITE_BRAND as string | undefined)?.trim() || 'Store';

type NavItem = {
  to: string;
  label: string;
  icon: typeof Home;
  /** Hidden until the member owns an active slot. */
  postSlot?: boolean;
  /** NavLink `end` — exact-match active (Dashboard, Store). */
  exact?: boolean;
  /** Only shown when messaging is enabled tenant-wide. */
  messagingOnly?: boolean;
  /** Only shown when wallet top-ups are enabled tenant-wide. */
  topupOnly?: boolean;
  /** Only shown when auto-ship is enabled tenant-wide. */
  autoshipOnly?: boolean;
  /** Only shown when the member subscription is enabled tenant-wide. */
  subscriptionOnly?: boolean;
  /** Only shown when the AI Assistant is enabled tenant-wide. */
  assistantOnly?: boolean;
  /** Only shown when the Resources library is enabled tenant-wide. */
  resourcesOnly?: boolean;
  /** Only shown when gift checks are enabled tenant-wide; renamed to the tenant's word. */
  vouchersOnly?: boolean;
  /**
   * LMS: hidden in learning mode (`member_dashboard_mode === 'learning'`) —
   * the MLM-presentation items the classroom handoff drops (Genealogy, Vault,
   * Redeem, Leads, Live Links, Training videos).
   */
  mlmOnly?: boolean;
  /** LMS: replaces `label` / `to` in learning mode (Store→Catalog, …). */
  learning?: { label: string; to?: string };
  /** LMS: shown ONLY in learning mode — the classroom-only surfaces. */
  learningOnly?: boolean;
};

type NavSection = { title: string; items: NavItem[] };

// Section-grouped navigation (Variation B). Each item keeps its lifecycle flags;
// a section with no visible items renders no eyebrow.
const SECTIONS: NavSection[] = [
  {
    title: 'Main',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: Home, exact: true },
      // The AI Assistant — right under Dashboard: the client may make this the
      // platform's headline feature, so it sits top-of-nav when enabled.
      { to: '/assistant', label: 'Assistant', icon: Sparkles, assistantOnly: true },
      // "Store" links out to the public buyer storefront. For a tenant WITH a
      // bespoke marketing landing (mhyel), `/` is that landing, so Store points
      // at `/storefront` (the shoppable home); otherwise `/` IS the storefront.
      {
        to: HAS_BESPOKE_LANDING ? '/storefront' : '/',
        label: 'Store',
        icon: ShoppingBag,
        exact: true,
        learning: { label: 'Catalog', to: '/courses' },
      },
      { to: '/orders', label: 'Orders', icon: ShoppingCart },
      // No slot needed: a shopper earns and spends gift checks at the counter.
      { to: '/gift-checks', label: 'Gift Checks', icon: Ticket, vouchersOnly: true },
      { to: '/auto-ship', label: 'Auto-Ship', icon: Repeat, postSlot: true, autoshipOnly: true },
      { to: '/messages', label: 'Messages', icon: MessageCircle, messagingOnly: true },
    ],
  },
  {
    title: 'Network',
    items: [
      { to: '/genealogy', label: 'Genealogy', icon: Network, postSlot: true, mlmOnly: true },
      { to: '/invite', label: 'Invite', icon: Share2, postSlot: true },
      { to: '/leads', label: 'Leads', icon: Users, postSlot: true, mlmOnly: true },
    ],
  },
  {
    title: 'Money',
    items: [
      { to: '/code-vault', label: 'Vault', icon: KeyRound, mlmOnly: true },
      { to: '/redeem', label: 'Redeem', icon: Gift, postSlot: true, mlmOnly: true },
      { to: '/wallet/top-up', label: 'Top Up', icon: Wallet, postSlot: true, topupOnly: true },
      { to: '/cash-out', label: 'Cash Out', icon: Banknote, postSlot: true },
      {
        to: '/earnings',
        label: 'Earnings',
        icon: TrendingUp,
        postSlot: true,
        learning: { label: 'Rewards' },
      },
    ],
  },
  {
    title: 'Learn',
    items: [
      // LMS: in learning mode this becomes "My Courses" (the course player home);
      // the training-videos page stays MLM-only.
      {
        to: '/training-videos',
        label: 'Training',
        icon: Video,
        postSlot: true,
        learning: { label: 'My Courses', to: '/my-courses' },
      },
      // A credential outlives the enrollment that earned it, so it gets its own
      // destination rather than living only at the bottom of My Courses — where
      // it also rendered nothing at all until the first certificate existed.
      {
        to: '/my-certificates',
        label: 'Certificates',
        icon: Award,
        learningOnly: true,
      },
      { to: '/resources', label: 'Resources', icon: FileText, postSlot: true, resourcesOnly: true },
      {
        to: '/subscribe',
        label: 'Subscribe',
        icon: CalendarClock,
        postSlot: true,
        subscriptionOnly: true,
      },
      {
        to: '/special-links',
        label: 'Live Links',
        icon: Radio,
        subscriptionOnly: true,
        mlmOnly: true,
      },
      // Always available (no slot needed): lets logged-in members reach the
      // feedback form without hunting for the util-strip link.
      { to: '/feedback', label: 'Feedback', icon: MessageSquare },
    ],
  },
];

const ALL_NAV: NavItem[] = SECTIONS.flatMap((s) => s.items);
const SETTINGS_NAV: NavItem = { to: '/settings', label: 'Settings', icon: SettingsIcon };

/** Compact peso (₱48.2K) for the identity stat trio. */
function fmtCompact(value: string | number | null | undefined): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

/** A single nav row — reused across the desktop rail (expanded + collapsed) and
 * the mobile drawer. Active = `bg-o-l text-o`; Orders shows a count pill,
 * Messages a red unread dot. */
function NavItemLink({
  n,
  collapsed,
  orderActionCount,
  unreadCount,
  onClick,
}: {
  n: NavItem;
  collapsed?: boolean;
  orderActionCount: number;
  unreadCount: number;
  onClick?: () => void;
}) {
  const Icon = n.icon;
  const badge = n.to === '/orders' ? orderActionCount : 0;
  const showDot = n.to === '/messages' && unreadCount > 0;
  return (
    <NavLink
      to={n.to}
      end={n.exact}
      onClick={onClick}
      title={collapsed ? n.label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-[9px] px-3.5 py-2.5 text-sm transition-colors',
          collapsed && 'justify-center px-2',
          isActive
            ? 'bg-o-l font-semibold text-o'
            : 'font-medium text-muted-foreground hover:bg-muted hover:text-foreground',
        )
      }
    >
      <span className="relative flex-none">
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
        {collapsed && (badge > 0 || showDot) && (
          <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-card" />
        )}
      </span>
      {!collapsed && <span className="truncate">{n.label}</span>}
      {!collapsed && badge > 0 && (
        <span className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10.5px] font-semibold leading-none text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {!collapsed && showDot && (
        <span className="ml-auto h-[7px] w-[7px] rounded-full bg-red-500" />
      )}
    </NavLink>
  );
}

/**
 * One column of the identity stat row — all-ink value + caption. The value span
 * is `w-full` + `truncate` so a long value can't overflow the sidebar; the full
 * value is on the `title` tooltip. Earned / Affiliates only — the slot code gets
 * its own full-width line (see `SlotCodeLine`).
 */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center px-1">
      <span
        title={value}
        className="block w-full truncate text-center text-[15px] font-semibold tabular-nums text-foreground"
      >
        {value}
      </span>
      <span className="mt-1 text-[9.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

/**
 * The active slot code on its own full-width line under the member's name, shown
 * in full (mono) with a copy button — no longer squeezed into a third-column
 * stat. Renders nothing when there's no active slot.
 */
function SlotCodeLine({ code }: { code: string }) {
  if (!code || code === '—') return null;
  return (
    <div className="mt-2 flex w-full items-center justify-center gap-1">
      <span className="truncate font-mono text-[12.5px] font-medium tracking-tight text-foreground">
        {code}
      </span>
      <CopyButton
        value={code}
        title="Copy slot code"
        label="Slot code copied"
        className="h-5 w-5"
      />
    </div>
  );
}

/**
 * Light/dark switch. Renders only for a tenant whose theme ships a dark token
 * set — without one the portal has nothing honest to switch TO (see
 * `ThemeTokens.dark`), so the affordance stays hidden rather than half-working.
 */
function ThemeToggle() {
  const available = useDarkAvailable();
  const mode = useThemeMode((s) => s.mode);
  const toggle = useThemeMode((s) => s.toggle);
  if (!available) return null;
  const dark = mode === 'dark';
  return (
    <button
      onClick={toggle}
      className="flex h-9 w-9 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
    >
      {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
    </button>
  );
}

export function Layout() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { hasAnyActiveSlot, activeSlot, activeSlots } = useMySlots();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(
    () =>
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('member.sidebarCollapsed') === '1',
  );

  useEffect(() => {
    localStorage.setItem('member.sidebarCollapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  // Orders "needs action" badge — to-pay + to-receive + to-rate. Polled like the
  // notifications bell; also invalidated by mark-received / review-submit.
  const orderActionQ = useQuery({
    queryKey: ['orders', 'action-summary'],
    queryFn: getOrderActionSummary,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  // Slot-scope the sidebar badge: the active slot's orders PLUS un-slotted
  // (ecom/kit/pre-activation) orders — matching what the Orders page shows for
  // the current slot. Per-active-slot counts + an "another slot needs action"
  // total drive the slot switcher's notification (only ACTIVE slots — orders on
  // a cancelled slot are unreachable and would leave an uncleanable badge).
  const orderSummary = orderActionQ.data;
  const activeSlotId = activeSlot?.id ?? null;
  const orderActionCount =
    (activeSlotId ? (orderSummary?.bySlot[activeSlotId]?.total ?? 0) : 0) +
    (orderSummary?.unslotted.total ?? 0);
  const slotOrderBadges: Record<string, number> = {};
  let otherSlotsOrderCount = 0;
  for (const s of activeSlots) {
    const c = orderSummary?.bySlot[s.id]?.total ?? 0;
    slotOrderBadges[s.id] = c;
    if (s.id !== activeSlotId) otherSlotsOrderCount += c;
  }

  const messagingEnabled = useTenantConfig().data?.messagingEnabled ?? false;
  const assistantEnabled = useTenantConfig().data?.assistantEnabled ?? false;
  const resourcesEnabled = useTenantConfig().data?.memberResourcesEnabled ?? false;
  const autoshipEnabled = useTenantConfig().data?.autoshipEnabled ?? true;
  const subscriptionEnabled = useTenantConfig().data?.memberSubscriptionEnabled ?? false;
  const vouchersEnabled = useTenantConfig().data?.vouchersEnabled ?? false;
  const tutorialEnabled = useTutorialEnabled();
  const voucherLabel = useTenantConfig().data?.voucherLabel?.trim() || 'Gift Check';
  const voucherNavLabel = voucherLabel.endsWith('s') ? voucherLabel : `${voucherLabel}s`;
  // Runtime brand name (admin-editable), with the build-time BRAND as first-paint seed.
  const brandName = useBrand().brandName || BRAND;
  // Tenant logo (uploaded → theme.logoUrl → build-baked). When set, the sidebar
  // shows the brand mark; otherwise it falls back to the monogram + name below.
  const { logoUrl: brandLogoUrl } = useBrandLogo();
  // Shares the ['me'] cache (deduped with the meQuery below) — gates the Top Up nav.
  const walletTopupEnabled =
    useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 60_000 }).data?.tenantPolicy
      ?.walletTopupEnabled ?? false;

  // Messages unread dot — same query the header MessagesBell uses, so it dedupes.
  const unreadQ = useQuery({
    queryKey: ['chat', 'unread-count'],
    queryFn: getChatUnreadCount,
    enabled: messagingEnabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const unreadCount = unreadQ.data?.count ?? 0;

  // LMS learning mode: hide the MLM-only items and apply the relabels
  // (Store→Catalog, Training→My Courses, Earnings→Rewards). MLM mode is
  // byte-identical — `learning` never touches an item when the flag is off.
  const learningMode = useTenantConfig().data?.memberDashboardMode === 'learning';
  const applyMode = (n: NavItem): NavItem =>
    learningMode && n.learning
      ? { ...n, label: n.learning.label, to: n.learning.to ?? n.to }
      : n.vouchersOnly
        ? { ...n, label: voucherNavLabel }
        : n;
  const isVisible = (n: NavItem) =>
    // A relabeled item that RE-TARGETS a slot-free page (Training → /my-courses)
    // shows pre-slot in learning mode; a pure relabel (Earnings → Rewards) keeps
    // its slot gate because its page still needs one.
    (!n.postSlot || hasAnyActiveSlot || (learningMode && !!n.learning?.to)) &&
    (!n.mlmOnly || !learningMode) &&
    (!n.learningOnly || learningMode) &&
    (!n.messagingOnly || messagingEnabled) &&
    (!n.topupOnly || walletTopupEnabled) &&
    (!n.autoshipOnly || autoshipEnabled) &&
    (!n.subscriptionOnly || subscriptionEnabled) &&
    (!n.assistantOnly || assistantEnabled) &&
    (!n.resourcesOnly || resourcesEnabled) &&
    (!n.vouchersOnly || vouchersEnabled);
  const visibleSections = SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter(isVisible).map(applyMode),
  })).filter((s) => s.items.length > 0);

  // Bottom mobile tab bar: curated 5 per user state, so every tab is actionable
  // for that member (pre-slot members never see a dead Genealogy tab). When the
  // Assistant is enabled it takes the second slot — it's a primary destination,
  // and reaching it through the drawer on a phone made it feel like a footnote.
  // The bar stays at 5: the displaced tab (Earnings / Settings) is the one that
  // still has a home in the drawer.
  const mobileBottomRoutes = learningMode
    ? assistantEnabled
      ? ['/dashboard', '/assistant', '/', '/training-videos', '/orders']
      : ['/dashboard', '/', '/training-videos', '/orders', '/settings']
    : hasAnyActiveSlot
      ? assistantEnabled
        ? ['/dashboard', '/assistant', '/', '/genealogy', '/cash-out']
        : ['/dashboard', '/', '/genealogy', '/cash-out', '/earnings']
      : assistantEnabled
        ? ['/dashboard', '/assistant', '/', '/code-vault', '/orders']
        : ['/dashboard', '/', '/code-vault', '/orders', '/settings'];
  const mobileBottom = mobileBottomRoutes
    .map((to) => [...ALL_NAV, SETTINGS_NAV].find((n) => n.to === to))
    .filter((n): n is NavItem => Boolean(n))
    .map(applyMode);

  const initials = initialsFromName(user?.fullName ?? user?.email ?? 'ME', 'ME');
  const avatarSrc = user?.profilePictureUrl ?? undefined;

  // `username` isn't on the auth-store user; read it from /me (shares the same
  // ['me'] cache the Invite page uses, so this dedupes rather than re-fetches).
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 60_000 });
  const username = meQuery.data?.username ?? null;
  const displayName = user?.fullName ?? user?.email ?? null;

  // Lifetime earnings for the ACTIVE slot → EARNED stat. Reuses the same
  // ['wallet', activeSlotId] query the Dashboard/Earnings pages use, so it dedupes
  // rather than refetches and follows the slot the member has selected.
  const walletQ = useQuery({
    queryKey: ['wallet', activeSlot?.id],
    queryFn: () => getSlotWallet(activeSlot!.id),
    enabled: hasAnyActiveSlot && !!activeSlot?.id,
    staleTime: 60_000,
  });
  // `totalEarned` includes any imported legacy lifetime figure, so a migrated
  // member's EARNED stat matches what their old system showed.
  const totalEarnings = walletQ.data?.totalEarned ?? null;

  // DOWNLINE stat — the active slot's full subtree size. depth:1 keeps the
  // payload tiny; each node's descendantCount is the full subtree count.
  const downlineQ = useQuery({
    queryKey: ['genealogy-count', activeSlot?.id],
    queryFn: () => getGenealogy({ slotId: activeSlot!.id, view: 'unilevel', depth: 1 }),
    enabled: hasAnyActiveSlot && !!activeSlot?.id,
    staleTime: 60_000,
  });
  const downlineCount = (() => {
    const nodes = downlineQ.data?.nodes;
    if (!nodes?.length) return null;
    const root = nodes.find((n) => n.slotId === activeSlot?.id) ?? nodes.find((n) => n.depth === 0);
    return root ? root.descendantCount : null;
  })();

  const earnedDisplay = totalEarnings != null ? fmtCompact(totalEarnings) : '—';
  const downlineDisplay = downlineCount != null ? String(downlineCount) : '—';
  const slotDisplay = activeSlot?.slotCode ?? '—';

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ImpersonationBanner />
      <TermsReacceptGate />
      <UnpaidOrdersGate />
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden flex-col border-r bg-card transition-[width] duration-200 md:flex',
          collapsed ? 'w-16' : 'w-[268px]',
        )}
      >
        {collapsed ? (
          // Collapsed: avatar (→ profile) doubles as the expand affordance.
          <div className="flex h-16 flex-col items-center justify-center gap-1 border-b">
            <Link to="/settings" aria-label="Your profile" title={displayName ?? undefined}>
              <Avatar size="md" initials={initials} src={avatarSrc} />
            </Link>
            <button
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
              className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <PanelLeft className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <>
            {/* Brand row */}
            <div className="flex items-center gap-2.5 px-5 pb-1 pt-5">
              {brandLogoUrl ? (
                <img
                  src={brandLogoUrl}
                  alt={brandName}
                  className="h-[30px] w-auto max-w-[168px] flex-none object-contain object-left"
                />
              ) : (
                <>
                  <div className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[9px] bg-o text-[15px] font-bold text-white">
                    {brandName.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-[18px] font-bold tracking-[-0.02em] text-foreground">
                    {brandName}
                  </span>
                </>
              )}
              <span className="ml-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Members
              </span>
              <button
                onClick={() => setCollapsed(true)}
                aria-label="Collapse sidebar"
                className="ml-auto flex h-7 w-7 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
            {/* Identity block */}
            <div className="flex flex-col items-center px-5 pb-3.5 pt-3.5">
              <Link
                to="/settings"
                aria-label="Your profile"
                className="rounded-full border-2 border-o p-1"
              >
                <Avatar
                  size="lg"
                  initials={initials}
                  src={avatarSrc}
                  className="h-16 w-16 bg-o-l text-2xl text-o"
                />
              </Link>
              {displayName && (
                <div className="mt-3 max-w-full truncate text-center text-[15.5px] font-semibold text-foreground">
                  {displayName}
                </div>
              )}
              {username && (
                <div className="mt-0.5 truncate text-[12.5px] font-medium text-muted-foreground">
                  @{username}
                </div>
              )}
              <SlotCodeLine code={slotDisplay} />
            </div>
            {/* Earned / Affiliates */}
            <div className="flex items-stretch justify-between border-y px-6 py-3.5">
              <Stat label="Earned" value={earnedDisplay} />
              <Stat label="Affiliates" value={downlineDisplay} />
            </div>
          </>
        )}

        {/* Nav (section-grouped; eyebrows hidden when collapsed) */}
        <nav
          className={cn(
            'flex flex-1 flex-col overflow-y-auto',
            collapsed ? 'gap-1 px-2 py-3' : 'gap-0.5 px-3.5 py-3',
          )}
        >
          {visibleSections.map((sec, i) => (
            <div
              key={sec.title}
              className={cn(
                'flex flex-col',
                collapsed ? cn('gap-1', i > 0 && 'mt-2 border-t pt-2') : 'gap-0.5',
              )}
            >
              {!collapsed && (
                <div
                  className={cn(
                    'px-3.5 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground',
                    i === 0 ? 'pt-1' : 'pt-3',
                  )}
                >
                  {sec.title}
                </div>
              )}
              {sec.items.map((n) => (
                <NavItemLink
                  key={n.to}
                  n={n}
                  collapsed={collapsed}
                  orderActionCount={orderActionCount}
                  unreadCount={unreadCount}
                />
              ))}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="flex flex-col gap-0.5 border-t p-2">
          {tutorialEnabled && <TutorialNavButton collapsed={collapsed} />}
          <NavItemLink
            n={SETTINGS_NAV}
            collapsed={collapsed}
            orderActionCount={0}
            unreadCount={0}
          />
          <button
            onClick={onLogout}
            title={collapsed ? 'Sign out' : undefined}
            className={cn(
              'flex items-center gap-3 rounded-[9px] px-3.5 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-o-l hover:text-o',
              collapsed && 'justify-center px-2',
            )}
          >
            <LogOut className="h-[18px] w-[18px] flex-none" strokeWidth={1.8} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div
        className={cn(
          'flex min-h-screen flex-col transition-[padding] duration-200',
          collapsed ? 'md:pl-16' : 'md:pl-[268px]',
        )}
      >
        {/* The member area uses the same storefront header as the public
            landing so the portal feels like one site. Portal-only controls
            (mobile sidebar toggle, Switch Slot, Notifications, avatar) are
            injected via the header's leading/actions slots.

            `stickyTop` pins the util strip together with the search row instead
            of letting it scroll away — on a phone the strip is the only place
            the "Shop Now" link lives, and the mobile app is a phone viewport by
            definition. */}
        <Header
          stickyTop
          utilLeftSlot={
            <Link to="/" className="cursor-pointer font-medium hover:text-o">
              Shop Now
            </Link>
          }
          leadingSlot={
            <button
              onClick={() => setMobileOpen(true)}
              className="flex h-9 w-9 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted md:hidden"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          }
          actionsSlot={
            <div className="flex flex-none items-center gap-1">
              <ThemeToggle />
              <SwitchSlotMenu slotBadges={slotOrderBadges} otherCount={otherSlotsOrderCount} />
              <MessagesBell />
              <NotificationsBell />
              <Link to="/settings" aria-label="Profile">
                <Avatar size="md" initials={initials} src={avatarSrc} />
              </Link>
            </div>
          }
        />

        <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 pb-24 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile nav drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 overflow-y-auto">
          <div className="mb-5 mt-2 flex flex-col items-center text-center">
            <Link
              to="/settings"
              aria-label="Your profile"
              onClick={() => setMobileOpen(false)}
              className="rounded-full border-2 border-o p-1"
            >
              <Avatar
                size="lg"
                initials={initials}
                src={avatarSrc}
                className="h-14 w-14 bg-o-l text-xl text-o"
              />
            </Link>
            {displayName && (
              <div className="mt-2.5 max-w-full truncate text-sm font-semibold">{displayName}</div>
            )}
            {username && <div className="truncate text-xs text-muted-foreground">@{username}</div>}
            <SlotCodeLine code={slotDisplay} />
          </div>
          <div className="mb-4 flex items-stretch justify-between border-y px-2 py-3">
            <Stat label="Earned" value={earnedDisplay} />
            <Stat label="Affiliates" value={downlineDisplay} />
          </div>
          <nav className="flex flex-col gap-0.5">
            {visibleSections.map((sec, i) => (
              <div key={sec.title} className="flex flex-col gap-0.5">
                <div
                  className={cn(
                    'px-3.5 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-muted-foreground',
                    i === 0 ? 'pt-1' : 'pt-3',
                  )}
                >
                  {sec.title}
                </div>
                {sec.items.map((n) => (
                  <NavItemLink
                    key={n.to}
                    n={n}
                    orderActionCount={orderActionCount}
                    unreadCount={unreadCount}
                    onClick={() => setMobileOpen(false)}
                  />
                ))}
              </div>
            ))}
            <div className="mt-2 flex flex-col gap-0.5 border-t pt-2">
              {tutorialEnabled && <TutorialNavButton onClick={() => setMobileOpen(false)} />}
              <NavItemLink
                n={SETTINGS_NAV}
                orderActionCount={0}
                unreadCount={0}
                onClick={() => setMobileOpen(false)}
              />
              <button
                onClick={() => {
                  setMobileOpen(false);
                  void onLogout();
                }}
                className="flex items-center gap-3 rounded-[9px] px-3.5 py-2.5 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-o-l hover:text-o"
              >
                <LogOut className="h-[18px] w-[18px] flex-none" strokeWidth={1.8} />
                Sign out
              </button>
            </div>
          </nav>
        </SheetContent>
      </Sheet>

      {/* Cart side sheet — globally mounted so any page can open via useCartUiStore. */}

      {/* Mobile bottom tab bar */}
      {/* data-bottom-bar: `useFillHeight` measures this instead of guessing it. */}
      <nav
        data-bottom-bar
        className="fixed inset-x-0 bottom-0 z-40 flex border-t bg-card md:hidden"
      >
        {mobileBottom.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              className={({ isActive }) =>
                cn(
                  'relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px]',
                  isActive ? 'text-o' : 'text-muted-foreground',
                )
              }
            >
              <span className="relative">
                <Icon className="h-5 w-5" />
                {n.to === '/orders' && orderActionCount > 0 && (
                  <span className="absolute -right-2 -top-1 inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-semibold leading-none text-white">
                    {orderActionCount > 99 ? '99+' : orderActionCount}
                  </span>
                )}
              </span>
              {n.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
